import { EVENT_SCOPES } from '../events/labels';

/** As the rules API sends it (module `rules`; the web app does not import server modules). */
export type TriggerType = 'item' | 'category' | 'kit' | 'scope';
/** Gear (`required`, `suggested`), a `reminder`; P15: a `service` in the quote, places in the `crew`. */
export type Hardness = 'required' | 'suggested' | 'reminder' | 'service' | 'crew';
export type Mode = 'fixed' | 'per';
export type Rule = {
  id: string;
  name: string;
  /** Review M-e: `setting` — kept by the owner's „Zakres → funkcja” (changed only there). */
  source?: string | null;
  triggerType: TriggerType;
  triggerRef: string;
  triggerName?: string;
  /** Category of the trigger device (an item trigger); null otherwise. Comes with `triggerRef`. */
  triggerCategoryId?: string | null;
  effectItemId: string | null;
  effectItemName?: string | null;
  /** P15: the price list's service a `service` rule adds to the quote. */
  effectServiceId?: string | null;
  effectServiceName?: string | null;
  /** P15: the crew function a `crew` rule wants `addQuantity` people of. */
  effectFunctionId?: string | null;
  effectFunctionName?: string | null;
  mode: Mode;
  addQuantity: number;
  perQuantity: number | null;
  hardness: Hardness;
  message: string;
  active: boolean;
  /** A reminder is due this many days before the event's first day. */
  reminderDaysBefore: number;
  version: number;
};

export const RULES_RESOURCE = 'rules.rules';
export const RULES_KEY = ['rules'] as const;

export const HARDNESS_LABELS: Record<Hardness, string> = { required: 'Wymagana', suggested: 'Sugerowana', reminder: 'Przypomnienie', service: 'Usługa w kosztorysie', crew: 'Miejsce w obsadzie' };
/**
 * What the editor offers as a trigger (P4, owner remark 3/21): equipment = a category, then any device of it
 * (a `category` trigger) or one device (an `item` trigger); a kit; a scope of works.
 */
export type TriggerKind = 'equipment' | 'kit' | 'scope';
export const TRIGGER_KIND_LABELS: Record<TriggerKind, string> = { equipment: 'Sprzęt (kategoria lub konkretne urządzenie)', kit: 'Zestaw', scope: 'Zakres prac' };
export const DEFAULT_REMINDER_DAYS = 3;
export const MAX_REMINDER_DAYS = 365;

/** "3 dni przed", "1 dzień przed", "w pierwszym dniu" (of the event). */
export function reminderLead(days: number): string {
  if (days === 0) return 'w pierwszym dniu wydarzenia';
  return `${days} ${days === 1 ? 'dzień' : 'dni'} przed`;
}
export const SCOPES = Object.entries(EVENT_SCOPES).map(([value, label]) => ({ value, label }));

/** "Na każde 4 × Kolumna QSC K12 → 1 × Statyw", "Gdy jest Konsoleta → 1 × Stagebox", "Gdy zakres Realizacja → „Sprawdź rider”". */
export function describeRule(
  r: Pick<Rule, 'triggerType' | 'triggerName' | 'effectItemName' | 'effectServiceName' | 'effectFunctionName' | 'mode' | 'addQuantity' | 'perQuantity' | 'hardness' | 'message'> & { reminderDaysBefore?: number },
): string {
  const trigger = r.triggerName ?? '…';
  const when = r.triggerType === 'scope' ? `Gdy zakres prac: ${trigger}` : r.triggerType === 'category' ? `Gdy jest coś z kategorii ${trigger}` : `Gdy jest ${trigger}`;
  if (r.hardness === 'reminder') return `${when} → „${r.message}”${r.reminderDaysBefore === undefined ? '' : ` (${reminderLead(r.reminderDaysBefore)})`}`;
  const effect =
    r.hardness === 'service'
      ? `${r.addQuantity} × ${r.effectServiceName ?? '…'} w kosztorysie`
      : r.hardness === 'crew'
        ? `${r.addQuantity} × ${r.effectFunctionName ?? '…'} w obsadzie`
        : `${r.addQuantity} × ${r.effectItemName ?? '…'}`;
  if (r.mode === 'per' && r.perQuantity) return `Na każde ${r.perQuantity} × ${trigger} → ${effect}`;
  return `${when} → ${effect}`;
}

export type RuleDraft = {
  name: string;
  hardness: Hardness;
  triggerKind: TriggerKind;
  /** Equipment: the category, and whether any device of it fires the rule or one (`triggerRef`). */
  categoryId: string;
  categoryName: string;
  device: 'any' | 'one';
  /** The device (equipment, `one`), the kit or the scope code. */
  triggerRef: string;
  triggerName: string;
  effectItemId: string | null;
  effectItemName: string;
  effectServiceId: string | null;
  effectServiceName: string;
  effectFunctionId: string | null;
  effectFunctionName: string;
  mode: Mode;
  addQuantity: string;
  perQuantity: string;
  message: string;
  active: boolean;
  reminderDaysBefore: string;
};

export const emptyDraft = (): RuleDraft => ({
  name: '',
  hardness: 'required',
  triggerKind: 'equipment',
  categoryId: '',
  categoryName: '',
  device: 'any',
  triggerRef: '',
  triggerName: '',
  effectItemId: null,
  effectItemName: '',
  effectServiceId: null,
  effectServiceName: '',
  effectFunctionId: null,
  effectFunctionName: '',
  mode: 'fixed',
  addQuantity: '1',
  perQuantity: '',
  message: '',
  active: true,
  reminderDaysBefore: String(DEFAULT_REMINDER_DAYS),
});

/** A field the role does not see is missing from `r`: the draft gets the default (and `bodyOf` never sends it). */
export const draftOf = (r: Rule): RuleDraft => {
  const d = emptyDraft();
  const type = r.triggerType ?? 'item';
  const category = type === 'category';
  return {
    name: r.name ?? d.name,
    hardness: r.hardness ?? d.hardness,
    triggerKind: type === 'kit' || type === 'scope' ? type : 'equipment',
    categoryId: (category ? r.triggerRef : r.triggerCategoryId) ?? '',
    categoryName: category ? (r.triggerName ?? '') : '',
    device: category ? 'any' : 'one',
    triggerRef: category ? '' : (r.triggerRef ?? d.triggerRef),
    triggerName: category ? '' : (r.triggerName ?? ''),
    effectItemId: r.effectItemId ?? null,
    effectItemName: r.effectItemName ?? '',
    effectServiceId: r.effectServiceId ?? null,
    effectServiceName: r.effectServiceName ?? '',
    effectFunctionId: r.effectFunctionId ?? null,
    effectFunctionName: r.effectFunctionName ?? '',
    mode: r.mode ?? d.mode,
    addQuantity: r.addQuantity === undefined ? d.addQuantity : String(r.addQuantity),
    perQuantity: r.perQuantity === null || r.perQuantity === undefined ? '' : String(r.perQuantity),
    message: r.message ?? '',
    active: r.active ?? d.active,
    reminderDaysBefore: r.reminderDaysBefore === undefined ? d.reminderDaysBefore : String(r.reminderDaysBefore),
  };
};

const count = (s: string): number | null => (/^\d+$/.test(s.trim()) && Number(s) > 0 && Number(s) <= 1_000_000 ? Number(s) : null);

/** The trigger as the API takes it, or the problem to show. */
function triggerOf(d: RuleDraft): { ok: true; triggerType: TriggerType; triggerRef: string } | { ok: false; error: string } {
  if (d.triggerKind !== 'equipment') {
    if (d.triggerRef === '') return { ok: false, error: 'Wybierz, co uruchamia regułę.' };
    return { ok: true, triggerType: d.triggerKind, triggerRef: d.triggerRef };
  }
  if (d.device === 'one') {
    if (d.triggerRef === '') return { ok: false, error: 'Wybierz urządzenie z kategorii.' };
    return { ok: true, triggerType: 'item', triggerRef: d.triggerRef };
  }
  if (d.categoryId === '') return { ok: false, error: 'Wybierz kategorię albo konkretne urządzenie.' };
  return { ok: true, triggerType: 'category', triggerRef: d.categoryId };
}

/** The rule as the API takes it, or the first problem to show (Polish). A reminder adds nothing; a scope counts once. */
type WireRule = Omit<Rule, 'id' | 'version' | 'triggerName' | 'triggerCategoryId' | 'effectItemName' | 'effectServiceName' | 'effectFunctionName'>;
export function wireOf(d: RuleDraft): { ok: true; rule: WireRule } | { ok: false; error: string } {
  if (d.name.trim() === '') return { ok: false, error: 'Wpisz nazwę reguły.' };
  const trigger = triggerOf(d);
  if (!trigger.ok) return trigger;
  const { triggerType, triggerRef } = trigger;
  const base = { name: d.name.trim(), hardness: d.hardness, triggerType, triggerRef, active: d.active };
  if (d.hardness === 'reminder') {
    if (d.message.trim() === '') return { ok: false, error: 'Wpisz treść przypomnienia.' };
    const days = /^\d+$/.test(d.reminderDaysBefore.trim()) ? Number(d.reminderDaysBefore) : NaN;
    if (!(days >= 0 && days <= MAX_REMINDER_DAYS)) return { ok: false, error: `Termin przypomnienia: od 0 do ${MAX_REMINDER_DAYS} dni przed wydarzeniem.` };
    return { ok: true, rule: { ...base, effectItemId: null, effectServiceId: null, effectFunctionId: null, mode: 'fixed', addQuantity: 1, perQuantity: null, message: d.message.trim(), reminderDaysBefore: days } };
  }
  // every kind carries its own effect only (the server refuses the others)
  const effects =
    d.hardness === 'service'
      ? { effectItemId: null, effectServiceId: d.effectServiceId, effectFunctionId: null }
      : d.hardness === 'crew'
        ? { effectItemId: null, effectServiceId: null, effectFunctionId: d.effectFunctionId }
        : { effectItemId: d.effectItemId, effectServiceId: null, effectFunctionId: null };
  if (d.hardness === 'service' && !d.effectServiceId) return { ok: false, error: 'Wybierz usługę z cennika.' };
  if (d.hardness === 'crew' && !d.effectFunctionId) return { ok: false, error: 'Wybierz funkcję w obsadzie.' };
  if ((d.hardness === 'required' || d.hardness === 'suggested') && !d.effectItemId) return { ok: false, error: 'Wybierz pozycję, którą reguła dodaje.' };
  const addQuantity = count(d.addQuantity);
  if (addQuantity === null) return { ok: false, error: 'Ilość musi być liczbą całkowitą większą od zera.' };
  const mode: Mode = triggerType === 'scope' ? 'fixed' : d.mode;
  const perQuantity = mode === 'per' ? count(d.perQuantity) : null;
  if (mode === 'per' && perQuantity === null) return { ok: false, error: '„Na każde” musi być liczbą całkowitą większą od zera.' };
  // the lead days only matter for a reminder: kept as they are (an edit does not send them)
  const reminderDaysBefore = /^\d+$/.test(d.reminderDaysBefore.trim()) ? Number(d.reminderDaysBefore) : DEFAULT_REMINDER_DAYS;
  return { ok: true, rule: { ...base, ...effects, mode, addQuantity, perQuantity, message: d.message.trim(), reminderDaysBefore } };
}

type Wire = Extract<ReturnType<typeof wireOf>, { ok: true }>['rule'];
const DEFAULTS: Partial<Wire> = { effectItemId: null, effectServiceId: null, effectFunctionId: null, mode: 'fixed', addQuantity: 1, perQuantity: null, message: '', active: true, reminderDaysBefore: DEFAULT_REMINDER_DAYS };

/**
 * What to send (rule 7a: only fields that are needed or changed). New rule: the required fields and those that
 * differ from the server's defaults. Edit: only the fields `base` has (the role sees them) and that differ from it.
 */
export function bodyOf(rule: Wire, base?: Rule): Record<string, unknown> {
  const keys = Object.keys(rule) as (keyof Wire)[];
  if (!base) return Object.fromEntries(keys.filter((k) => !(k in DEFAULTS) || DEFAULTS[k] !== rule[k]).map((k) => [k, rule[k]]));
  return { version: base.version, ...Object.fromEntries(keys.filter((k) => k in base && base[k] !== rule[k]).map((k) => [k, rule[k]])) };
}
